import dayjs from 'dayjs';
import ShelfController from '../../core/shelf.controller';
import { User } from '../../data';
import { canAccess } from '../../core/access';

const isCanceled = (e) => e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError';

export const CHANNEL_OPTIONS = [
    { label: 'Moderno', value: 'moderno' },
    { label: 'Tradicional', value: 'tradicional' }
];

export const DEFAULT_FILTERS = { channel: [], tagId: [], clientId: [], storeId: [], storeType: [] };

class NumericDistributionController extends ShelfController {
    async initialize() {
        if (!this.isAuthenticated) {
            this.setState({ loading: false });
            this.navigateLogin();
            return;
        }
        const user = User.getInstance();
        if (!canAccess(user?.userType, 'numeric-distribution')) {
            this.setState({ loading: false, accessDenied: true });
            return;
        }
        try {
            const [clients, tagsRes, storeTypesRes] = await Promise.all([
                this.data.fetchClients().catch(() => []),
                this.data.fetchTags().catch(() => null),
                this.data.fetchStoreTypes().catch(() => [])
            ]);
            const clientOptions = (clients || [])
                .filter((c) => c?._id && c?.name)
                .map((c) => ({ label: c.name, value: c._id }))
                .sort((a, b) => a.label.localeCompare(b.label));
            const tagOptions = (tagsRes?.tags || [])
                .filter((t) => t?._id && (t.applicableModels || []).some((m) => ['Client', 'Store'].includes(m)))
                .map((t) => ({ label: t.name, value: t._id, color: t.color }));
            const storeTypeOptions = (storeTypesRes || [])
                .filter((t) => t?.value && t?.active !== false)
                .map((t) => ({ label: t.label || t.value, value: t.value }))
                .sort((a, b) => a.label.localeCompare(b.label));
            this.setState({ clientOptions, tagOptions, storeTypeOptions, loading: false });
            await this.loadOverview();
        } catch (err) {
            this.setState({ loading: false, error: true });
        }
    }

    retry() {
        this.setState({ loading: true, error: false }, () => this.initialize());
    }

    // Date window as UTC calendar-day boundaries. Store-check createdAt is stored as Venezuela local
    // time cast to UTC, so a plain YYYY-MM-DD..T23:59:59.999Z end always includes the whole end day
    // (today included) regardless of the browser timezone.
    dateParams() {
        const { dateRange } = this.state;
        const q = {};
        if (dateRange?.[0] && dateRange?.[1]) {
            q.startDate = `${dateRange[0].format('YYYY-MM-DD')}T00:00:00.000Z`;
            q.endDate = `${dateRange[1].format('YYYY-MM-DD')}T23:59:59.999Z`;
        }
        return q;
    }

    // Filters shared by every request (dates + channel + tag). Client/store scope is added per call.
    commonParams() {
        const { filters } = this.state;
        const q = { ...this.dateParams() };
        if (filters.channel?.length) q.channel = filters.channel.join(',');
        if (filters.tagId?.length) q.tagId = filters.tagId.join(',');
        if (filters.storeId?.length) q.storeId = filters.storeId.join(',');
        if (filters.storeType?.length) q.storeType = filters.storeType.join(',');
        return q;
    }

    // Scope the PDV options to the currently-selected client(s) — avoids a 25k-store select.
    async loadStoreOptions(clientIds) {
        if (!clientIds?.length) {
            this.setState({ storeOptions: [] });
            return;
        }
        try {
            const lists = await Promise.all(clientIds.map((id) => this.data.fetchStoresByClient(id).catch(() => [])));
            const seen = new Set();
            const storeOptions = lists
                .flat()
                .filter((s) => s?._id && s?.name && !seen.has(String(s._id)) && seen.add(String(s._id)))
                .map((s) => ({ label: s.name, value: String(s._id) }))
                .sort((a, b) => a.label.localeCompare(b.label));
            this.setState({ storeOptions });
        } catch {
            this.setState({ storeOptions: [] });
        }
    }

    async loadOverview() {
        this._ovAbort?.abort();
        const abort = new AbortController();
        this._ovAbort = abort;
        this.setState({ dataLoading: true });
        try {
            const { filters } = this.state;
            const params = { ...this.commonParams() };
            if (filters.clientId?.length) params.clientId = filters.clientId.join(',');
            const res = await this.data.fetchNumericDistribution(params, abort.signal);
            if (this._ovAbort !== abort) return;
            this.setState({ data: res?.data || null, dataLoading: false });
        } catch (err) {
            if (this._ovAbort !== abort || isCanceled(err)) return;
            this.props.onMessage?.('error', 'No se pudo cargar la Distribución Numérica');
            this.setState({ dataLoading: false });
        }
    }

    async loadStores() {
        const { selectedClientId, table2Product } = this.state;
        if (!selectedClientId) {
            this.setState({ clientStores: null, productOptions: [] });
            return;
        }
        this._stAbort?.abort();
        const abort = new AbortController();
        this._stAbort = abort;
        this.setState({ storesLoading: true });
        try {
            // Presence for the picked product is resolved server-side (productByMonth); the response also
            // carries the product universe seen in this client's checks, which feeds the filter options.
            const params = { ...this.commonParams(), clientId: selectedClientId };
            if (table2Product) params.productId = table2Product;
            const res = await this.data.fetchNumericDistributionStores(params, abort.signal);
            if (this._stAbort !== abort) return;
            this.setState({ clientStores: res?.data || null, productOptions: res?.data?.products || [], storesLoading: false });
        } catch (err) {
            if (this._stAbort !== abort || isCanceled(err)) return;
            this.props.onMessage?.('error', 'No se pudieron cargar los PDV del cliente');
            this.setState({ storesLoading: false });
        }
    }

    setFilter(key, value) {
        this.setState(
            (prev) => {
                const filters = { ...prev.filters, [key]: value };
                // Changing the client scope resets the PDV selection (its options are client-scoped).
                if (key === 'clientId') filters.storeId = [];
                return { filters };
            },
            () => {
                if (key === 'clientId') {
                    this.loadStoreOptions(value || []);
                    // Convenience: selecting exactly one client up top also drives the PDV table below,
                    // so the client doesn't have to be picked twice. Multi/empty selection leaves it as-is.
                    if ((value || []).length === 1) {
                        const only = value[0];
                        this.selectClient(only, this.state.clientOptions.find((o) => o.value === only)?.label);
                    }
                }
                this.loadOverview();
                this.loadStores();
            }
        );
    }

    setDateRange(dateRange) {
        this.setState({ dateRange }, () => {
            this.loadOverview();
            this.loadStores();
        });
    }

    clearFilters() {
        this.setState(
            {
                filters: { ...DEFAULT_FILTERS },
                storeOptions: [],
                dateRange: [dayjs().subtract(150, 'day'), dayjs()],
                selectedClientId: null,
                selectedClientName: null,
                clientStores: null,
                productOptions: [],
                table2Product: null
            },
            () => this.loadOverview()
        );
    }

    selectClient(clientId, clientName) {
        if (!clientId) return;
        // Product options are per client, so a client switch drops the current product selection.
        this.setState(
            { selectedClientId: clientId, selectedClientName: clientName || '', clientStores: null, productOptions: [], table2Product: null },
            () => this.loadStores()
        );
    }

    // "Cliente" select inside the PDV card: selecting loads its PDV; clearing empties the table.
    pickTable2Client(v) {
        const { clientOptions } = this.state;
        if (v) this.selectClient(v, clientOptions.find((o) => o.value === v)?.label);
        else this.setState({ selectedClientId: null, clientStores: null });
    }

    setTable2Product(productId) {
        this.setState({ table2Product: productId || null }, () => this.loadStores());
    }

    openStore(store) {
        this.setState({ drawerOpen: true, drawerStore: store });
    }

    closeStore() {
        this.setState({ drawerOpen: false });
    }

    // ---- Table 2: PDV of the selected client — presence per month ----
    // Resolve each store's per-month presence for the selected product (or "any product").
    get storeRows() {
        const { clientStores, table2Product } = this.state;
        const stores = clientStores?.stores || [];
        const months = clientStores?.months || [];
        return stores.map((s) => {
            const byMonth = {};
            let current = null;
            for (const m of months) {
                const audited = !!s.audited?.[m];
                const present = table2Product ? !!s.productByMonth?.[m] : !!s.anyByMonth?.[m];
                byMonth[m] = { audited, present };
                if (audited) current = present;
            }
            return { ...s, byMonth, current };
        });
    }

    // Label of the product picked for Table 2 (falls back to its id).
    table2ProductLabel() {
        const { productOptions, table2Product } = this.state;
        return productOptions.find((o) => o.value === table2Product)?.label || table2Product;
    }
}

export default NumericDistributionController;
